import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiUrl, API_ENDPOINTS } from '../../utils/api-config';
import { useCustomer } from '../../context/CustomerContext';
import { customerApi } from '../../services/api';
import type { Framework, FrameworkItemRequest } from '../../types';

interface FrameworksTabProps {
    dashboard?: boolean;
}

type ToolsKey =
    | 'asset-collection'
    | 'patch-management'
    | 'change-tracking'
    | 'additional-tools'
    | 'inventory-management'
    | 'endpoint-control'
    | 'lifecycle-control'
    | 'additional-tools-inventory'
    | 'identity-access'
    | 'vuln-management'
    | 'observability'
    | 'additional-tools-verification'
    | 'document-repo'
    | 'diagrams'
    | 'additional-docs'
    | 'security-frameworks'
    | 'additional-compliance'
    | 'government-regulations'
    | 'industry-regulations'
    | 'additional-regulations'
    | 'empty1'
    | 'empty2'
    | 'empty3'
    | 'empty4';

type FocusType = 'operational' | 'security';

/** Map from (category_name, division_name) to ToolsKey for building divisionIdMap from API data. */
const CATEGORY_DIVISION_TO_KEY: Record<string, ToolsKey> = {
    'Identification|Asset Collection': 'asset-collection',
    'Identification|Patch Management': 'patch-management',
    'Identification|Change Tracking': 'change-tracking',
    'Identification|Additional Tools': 'additional-tools',
    'Inventory|Inventory Mgmt': 'inventory-management',
    'Inventory|Inventory Management': 'inventory-management',
    'Inventory|Endpoint Control': 'endpoint-control',
    'Inventory|Lifecycle Control': 'lifecycle-control',
    'Inventory|Additional Tools': 'additional-tools-inventory',
    'Verification|Identity Access': 'identity-access',
    'Verification|Vuln Management': 'vuln-management',
    'Verification|Observability': 'observability',
    'Verification|Additional Verification': 'additional-tools-verification',
    'Documentation|Document Repo': 'document-repo',
    'Documentation|Diagrams': 'diagrams',
    'Documentation|Additional Docs': 'additional-docs',
    'Compliance|Security Frameworks': 'security-frameworks',
    'Compliance|Additional Compliance': 'additional-compliance',
    'Regulations|Government Regulations': 'government-regulations',
    'Regulations|Industry Regulations': 'industry-regulations',
    'Regulations|Additional Regulations': 'additional-regulations',
};

export interface DivisionIdMapEntry {
    framework_id: string;
    focus: string;
    category_id: string;
    category_name: string;
    division_id: string;
    division_name: string;
}

function buildToolsAndMapFromFrameworks(
    frameworks: Framework[] | Array<Record<string, unknown>>
): { tools: Record<ToolsKey, string[]>; divisionIdMap: Partial<Record<ToolsKey, DivisionIdMapEntry>> } {
    const tools: Record<ToolsKey, string[]> = {
        'asset-collection': [], 'patch-management': [], 'change-tracking': [], 'additional-tools': [],
        'inventory-management': [], 'endpoint-control': [], 'lifecycle-control': [], 'additional-tools-inventory': [],
        'identity-access': [], 'vuln-management': [], 'observability': [], 'additional-tools-verification': [],
        'document-repo': [], 'diagrams': [], 'additional-docs': [],
        'security-frameworks': [], 'additional-compliance': [],
        'government-regulations': [], 'industry-regulations': [], 'additional-regulations': [],
        'empty1': [], 'empty2': [], 'empty3': [], 'empty4': [],
    };
    const divisionIdMap: Partial<Record<ToolsKey, DivisionIdMapEntry>> = {};
    for (const framework of frameworks) {
        const fw = framework as Framework & { framework_id?: string };
        const frameworkId = fw.framework_id ?? '';
        const focus = fw.focus ?? '';
        for (const category of fw.categories || []) {
            const cat = category as Framework['categories'][0] & { category_id?: string };
            const categoryId = cat.category_id ?? '';
            const categoryName = cat.category_name ?? '';
            for (const division of cat.divisions || []) {
                const div = division as { division_id?: string; division_name: string; metrics?: string[] };
                const divisionId = div.division_id ?? '';
                const divisionName = div.division_name ?? '';
                const key = CATEGORY_DIVISION_TO_KEY[`${categoryName}|${divisionName}`];
                if (key) {
                    tools[key] = Array.isArray(div.metrics) && div.metrics.length > 0 ? div.metrics : ['None'];
                    if (frameworkId && categoryId && divisionId && focus && categoryName && divisionName) {
                        divisionIdMap[key] = {
                            framework_id: frameworkId,
                            focus,
                            category_id: categoryId,
                            category_name: categoryName,
                            division_id: divisionId,
                            division_name: divisionName,
                        };
                    }
                }
            }
        }
    }
    return { tools, divisionIdMap };
}

const FrameworksTab: React.FC<FrameworksTabProps> = ({ dashboard }) => {
    const { customerData, updateCustomerData } = useCustomer();
    
    const [isLoading, setIsLoading] = useState(true);
    const [editCategory, setEditCategory] = useState<{ id: ToolsKey, name: string, focus: FocusType } | null>(null);
    const [divisionIdMap, setDivisionIdMap] = useState<Partial<Record<ToolsKey, DivisionIdMapEntry>>>({});
    const [tools, setTools] = useState<Record<ToolsKey, string[]>>({
        'asset-collection': [],
        'patch-management': [],
        'change-tracking': [],
        'additional-tools': [],
        'inventory-management': [],
        'endpoint-control': [],
        'lifecycle-control': [],
        'additional-tools-inventory': [],
        'identity-access': [],
        'vuln-management': [],
        'observability': [],
        'additional-tools-verification': [],
        'document-repo': [],
        'diagrams': [],
        'additional-docs': [],
        'security-frameworks': [],
        'additional-compliance': [],
        'government-regulations': [],
        'industry-regulations': [],
        'additional-regulations': [],
        'empty1': [],
        'empty2': [],
        'empty3': [],
        'empty4': []
    });
    const [inputBox, SetInputBox] = useState<Boolean>(false);

    const ToggelInputBox = () => {
        SetInputBox(prev => !prev);
    };


    const fetchFrameworksData = useCallback(async (frameworksOverride?: Framework[]) => {
        try {
            setIsLoading(true);
            const customerFrameworks = frameworksOverride ?? customerData?.frameworks?.frameworks;
            if (customerFrameworks && Array.isArray(customerFrameworks) && customerFrameworks.length > 0) {
                const { tools: newTools, divisionIdMap: newMap } = buildToolsAndMapFromFrameworks(customerFrameworks);
                setTools(newTools);
                setDivisionIdMap(newMap);
                return;
            }
            let data: { frameworks?: unknown[] } | null = null;
            try {
                const cached = localStorage.getItem('static_frameworks');
                if (cached) data = JSON.parse(cached) as { frameworks?: unknown[] };
            } catch {
                localStorage.removeItem('static_frameworks');
            }
            if (!data?.frameworks?.length) {
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken?.toString();
                if (!idToken) throw new Error('No ID token found in session');
                const base64Url = idToken.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                const decodedToken = JSON.parse(jsonPayload);
                const userId = decodedToken.sub || 'unknown';
                const response = await fetch(buildApiUrl(API_ENDPOINTS.STATIC_FRAMEWORKS), {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'user-id': userId,
                        Authorization: `Bearer ${idToken}`,
                    },
                });
                if (!response.ok) throw new Error(`Failed to fetch frameworks: ${response.status}`);
                data = await response.json();
                try {
                    localStorage.setItem('static_frameworks', JSON.stringify(data));
                } catch {
                    /* ignore */
                }
            }
            if (data?.frameworks && Array.isArray(data.frameworks)) {
                const { tools: newTools, divisionIdMap: newMap } = buildToolsAndMapFromFrameworks(data.frameworks as []);
                setTools(newTools);
                setDivisionIdMap(newMap);
            } else {
                setTools(prev => {
                    const next = { ...prev };
                    (Object.keys(next) as ToolsKey[]).forEach(k => { next[k] = ['None']; });
                    return next;
                });
            }
        } catch (err) {
            console.error('Error fetching frameworks data:', err);
            setTools(prev => {
                const next = { ...prev };
                (Object.keys(next) as ToolsKey[]).forEach(k => { next[k] = ['None']; });
                return next;
            });
        } finally {
            setIsLoading(false);
        }
    }, [customerData?.frameworks?.frameworks]);

    useEffect(() => {
        fetchFrameworksData();
    }, [fetchFrameworksData]);

    const updateSingleDivision = useCallback(
        async (divisionKey: ToolsKey, metrics: string[]) => {
            const customerId = customerData?.customer_id;
            if (!customerId) {
                throw new Error('Customer not loaded. Please refresh the page.');
            }
            const entry = divisionIdMap[divisionKey];
            if (!entry) {
                throw new Error('Framework data is incomplete. Please refresh the page and try again.');
            }
            const metricsArr = Array.isArray(metrics) ? metrics.filter(m => m !== 'None' && m !== '--') : [];
            const body: FrameworkItemRequest = {
                framework_id: entry.framework_id.trim(),
                focus: entry.focus.trim(),
                category_id: entry.category_id.trim(),
                category_name: entry.category_name.trim(),
                division_id: entry.division_id.trim(),
                division_name: entry.division_name.trim(),
                metrics: metricsArr,
            };
            if (!body.framework_id || !body.category_id || !body.division_id || !body.focus || !body.category_name || !body.division_name) {
                throw new Error('Framework IDs are missing. Please refresh the page to load framework data.');
            }
            await customerApi.createOrUpdateFrameworkItem(customerId, body);
            const response = await customerApi.getCustomerFramework(customerId);
            updateCustomerData({ frameworks: { frameworks: response.frameworks } });
            return response.frameworks;
        },
        [customerData?.customer_id, divisionIdMap, updateCustomerData]
    );

    const handleEditClick = (categoryId: ToolsKey, categoryName: string, focus: FocusType) => {

        
        // If the tools for this category are empty or just contain "None", set to empty array
        const categoryTools = tools[categoryId] || [];
        if (categoryTools.length === 0 || (categoryTools.length === 1 && (categoryTools[0] === 'None' || categoryTools[0] === '--'))) {
            const updatedTools = { ...tools };
            updatedTools[categoryId] = [];
            setTools(updatedTools);
        }
        
        setEditCategory({ id: categoryId, name: categoryName, focus });
    };

    const handleCloseEdit = useCallback(() => {
        setEditCategory(null);
    }, []);

    useEffect(() => {
        if (!editCategory) return;

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleCloseEdit();
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [editCategory, handleCloseEdit]);
    
    const handleAddTool = async (categoryId: ToolsKey, toolName: string) => {
        try {
            const updatedMetrics = [...(tools[categoryId] || []).filter(m => m !== 'None' && m !== '--'), toolName];
            setTools(prev => ({ ...prev, [categoryId]: updatedMetrics }));
            const updatedFrameworks = await updateSingleDivision(categoryId, updatedMetrics);
            SetInputBox(false);
            toast.success(`Tool "${toolName}" added successfully!`);
            if (updatedFrameworks) await fetchFrameworksData(updatedFrameworks);
        } catch (err) {
            console.error('Error adding tool:', err);
            toast.error('Failed to add tool. Please try again.');
            fetchFrameworksData();
        }
    };

    const handleRemoveTool = async (categoryId: ToolsKey, index: number) => {
        try {
            const toolName = tools[categoryId][index];
            const updatedMetrics = (tools[categoryId] || []).filter((_, i) => i !== index);
            const toSave = updatedMetrics.filter(m => m !== 'None' && m !== '--');
            setTools(prev => ({
                ...prev,
                [categoryId]: updatedMetrics.length === 0 ? ['None'] : updatedMetrics,
            }));
            const updatedFrameworks = await updateSingleDivision(categoryId, toSave.length ? toSave : []);
            toast.success(`Tool "${toolName}" removed successfully!`);
            if (updatedFrameworks) await fetchFrameworksData(updatedFrameworks);
        } catch (err) {
            console.error('Error removing tool:', err);
            toast.error('Failed to remove tool. Please try again.');
            fetchFrameworksData();
        }
    };

    // Function to render the tools for a category
    const renderTools = (categoryId: ToolsKey) => {
        const categoryTools = tools[categoryId] || [];

        if (categoryTools.length === 0 || (categoryTools.length === 1 && (categoryTools[0] === '--' || categoryTools[0] === 'None'))) {
            return <span className="text-gray-400"></span>;
        }

        if (categoryTools.length === 1 && categoryTools[0].toLowerCase() === 'no defined tools') {
            return <span>No defined tools</span>;
        }

        if (categoryTools.length === 1 && categoryTools[0].toLowerCase() === 'no standard') {
            return <span>No standard</span>;
        }

        if (categoryTools.length === 1 && categoryTools[0].toLowerCase() === 'none') {
            return <span>none</span>;
        }

        return (
            <div>
                {categoryTools.map((tool: string, idx: number) => (
                    <div key={idx} className="mb-1">{tool}</div>
                ))}
            </div>
        );
    };

    // Define the matrix structure for the main sections
    const matrixSections = [
        {
            id: 'identification',
            name: 'Identification',
            focus: 'operational' as FocusType,
            items: [
                { id: 'asset-collection' as ToolsKey, name: 'Asset Collection' },
                { id: 'patch-management' as ToolsKey, name: 'Patch management' },
                { id: 'change-tracking' as ToolsKey, name: 'Change Tracking' },
                { id: 'additional-tools' as ToolsKey, name: 'Additional Tools' },
            ]
        },
        {
            id: 'inventory',
            name: 'Inventory',
            focus: 'operational' as FocusType,
            items: [
                { id: 'inventory-management' as ToolsKey, name: 'Inventory Management' },
                { id: 'endpoint-control' as ToolsKey, name: 'Endpoint Control' },
                { id: 'lifecycle-control' as ToolsKey, name: 'Lifecycle Control' },
                { id: 'additional-tools-inventory' as ToolsKey, name: 'Additional Tools' },
            ]
        },
        {
            id: 'verification',
            name: 'Verification',
            focus: 'operational' as FocusType,
            items: [
                { id: 'identity-access' as ToolsKey, name: 'Identity Access' },
                { id: 'vuln-management' as ToolsKey, name: 'Vuln Management' },
                { id: 'observability' as ToolsKey, name: 'Observability' },
                { id: 'additional-tools-verification' as ToolsKey, name: 'Additional Tools' },
            ]
        },
        {
            id: 'documentation',
            name: 'Documentation',
            focus: 'security' as FocusType,
            items: [
                { id: 'document-repo' as ToolsKey, name: 'Document Repo' },
                { id: 'diagrams' as ToolsKey, name: 'Diagrams' },
                { id: 'additional-docs' as ToolsKey, name: 'Additional Docs' },
                { id: 'empty2' as ToolsKey, name: '' },
            ]
        },
        {
            id: 'compliance',
            name: 'Compliance',
            focus: 'security' as FocusType,
            items: [
                { id: 'security-frameworks' as ToolsKey, name: 'Security Frameworks' },
                { id: 'additional-compliance' as ToolsKey, name: 'Additional Compliance' },
                { id: 'empty1' as ToolsKey, name: '' },
                { id: 'empty3' as ToolsKey, name: '' },
            ]
        },
        {
            id: 'regulations',
            name: 'Regulations',
            focus: 'security' as FocusType,
            items: [
                { id: 'government-regulations' as ToolsKey, name: 'Government Regulations' },
                { id: 'industry-regulations' as ToolsKey, name: 'Industry Regulations' },
                { id: 'additional-regulations' as ToolsKey, name: 'Additional Regulations' },
                { id: 'empty4' as ToolsKey, name: '' },
            ]
        }
    ];

    const operationalSections = matrixSections.filter(section => section.focus === 'operational');
    const securitySections = matrixSections.filter(section => section.focus === 'security');

    if (isLoading) {
        return (
            <div className="flex justify-center py-10 items-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
            </div>
        );
    }

    return (
        <div className="w-full bg-transparent">
            <div className="w-full">
                {/* Focus Headers */}
                <div className="grid grid-cols-6">
                    <div
                        className="col-span-3 py-3 text-center text-[#002D72] text-lg font-semibold"
                        style={{
                            background: 'rgba(232, 241, 251, 0.7)', // Accent/Blues/Lightest
                            backdropFilter: 'blur(5px)',
                            WebkitBackdropFilter: 'blur(5px)'
                        }}
                    >
                        Operational Focus
                    </div>
                    <div className="col-span-3 py-3 text-center text-[#002D72] text-lg font-semibold"
                        style={{
                            background: 'rgba(232, 241, 251, 0.7)', // Accent/Blues/Lightest
                            backdropFilter: 'blur(5px)',
                            WebkitBackdropFilter: 'blur(5px)'
                        }}>
                        Security Focus
                    </div>
                </div>

                {/* Category Headers */}
                <div className="grid grid-cols-6 border-y  border-gray-300">
                    {operationalSections.map((section, index) => (
                        <div
                            key={section.id}
                            className="col py-3.5 px-4 text-center font-medium text-[#062E5E]"
                            style={{
                                background: 'linear-gradient(90deg, #0081BC14 0%, #E8F1FB 100%)',
                                borderWidth: '1px 1px 0px 1px',
                                borderStyle: 'solid',
                                borderColor: '#D8D8D8',
                            }}
                        >
                            {section.name}
                        </div>
                    ))}
                    {securitySections.map((section) => (
                        <div
                            key={section.id}
                            className="col py-3.5 px-4 text-center font-medium text-[#062E5E] border-1"
                            style={{
                                background: 'linear-gradient(90deg, #0081BC14 0%, #E8F1FB 100%)',
                                borderWidth: '1px 1px 0px 1px',
                                borderStyle: 'solid',
                                borderColor: '#D8D8D8',
                            }}
                        >
                            {section.name}
                        </div>
                    ))}
                </div>

                {/* Generate rows from items - each row takes one item from each section */}
                {[0, 1, 2, 3].map((rowIndex) => (
                    <React.Fragment key={`row-group-${rowIndex}`}>
                        {/* Header row with item name and edit button */}
                        <div className="grid grid-cols-6 border-b border-gray-300 bg-[#9fc7f9]/20 text-[#062E5E]">
                            {operationalSections.map((section, sectionIndex) => {
                                const item = section.items[rowIndex];

                                if (!item || item.name === '') {
                                    return (
                                        <div
                                            key={`${section.id}-header-${rowIndex}`}
                                            className={`col p-2 border-r border-gray-300`}
                                        />
                                    );
                                }

                                return (
                                    <div
                                        key={`${section.id}-header-${rowIndex}`}
                                        className={`col p-2 border-r border-gray-300`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="font-medium text-sm">{item.name}</div>
                                            {
                                                !dashboard && (<button
                                                onClick={() => handleEditClick(item.id, item.name, section.focus)}
                                                className="text-gray-600 hover:text-blue-800"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                </svg>
                                            </button>)
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                            {securitySections.map((section) => {
                                const item = section.items[rowIndex];

                                if (!item || item.name === '') {
                                    return (
                                        <div
                                            key={`${section.id}-header-${rowIndex}`}
                                            className="col p-2 border-r last:border-r-0 border-gray-300"
                                        />
                                    );
                                }

                                return (
                                    <div
                                        key={`${section.id}-header-${rowIndex}`}
                                        className="col p-2 border-r last:border-r-0 border-gray-300"
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="font-medium text-sm">{item.name}</div>
                                              {
                                                !dashboard && (<button
                                                onClick={() => handleEditClick(item.id, item.name, section.focus)}
                                                className="text-gray-600 hover:text-blue-800"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                </svg>
                                            </button>)
                                            }
                                            
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Content row with actual tools */}
                        <div className="grid grid-cols-6 border-b border-gray-300 bg-transparent">
                            {operationalSections.map((section, sectionIndex) => {
                                const item = section.items[rowIndex];

                                if (!item || item.name === '') {
                                    return (
                                        <div
                                            key={`${section.id}-content-${rowIndex}`}
                                            className={`col p-2 border-r border-gray-300 `}
                                        />
                                    );
                                }

                                return (
                                    <div
                                        key={`${section.id}-content-${rowIndex}`}
                                        className={`col p-2 border-r border-gray-300 `}
                                    >
                                        <div className="text-sm">
                                            {renderTools(item.id)}
                                        </div>
                                    </div>
                                );
                            })}
                            {securitySections.map((section) => {
                                const item = section.items[rowIndex];

                                if (!item || item.name === '') {
                                    return (
                                        <div
                                            key={`${section.id}-content-${rowIndex}`}
                                            className="col p-2 border-r last:border-r-0 border-gray-300"
                                        />
                                    );
                                }

                                return (
                                    <div
                                        key={`${section.id}-content-${rowIndex}`}
                                        className="col p-2 border-r last:border-r-0 border-gray-300"
                                    >
                                        <div className="text-sm">
                                            {renderTools(item.id)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {editCategory && (
                <div className="fixed inset-0 bg-white bg-opacity-30 flex justify-end z-50">
                    <div className="w-full bg-white max-w-md h-full shadow-lg overflow-y-auto p-6 animate-slide-in-right">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-[#002D72]">
                                {editCategory.focus === 'operational' ? 'Operational Focus' : 'Security Focus'}
                            </h2>
                            <button
                                onClick={handleCloseEdit}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="mb-6 bg-white">
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="font-medium">{editCategory.name}</h3>
                                {/* <p
                                    className='font-semibold text-gray-800 hover:text-gray-600 hover:cursor-pointer select-none'
                                    onClick={ToggelInputBox}
                                >
                                    + Add new Item
                                </p> */}
                                <button
                                    onClick={ToggelInputBox}
                                    className={`flex items-center border border-gray-200 rounded px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 ${!inputBox?"bg-white":"bg-gray-100"}`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add New Item
                                </button>
                            </div>

                            {
                                inputBox && (
                                    <div className="flex items-center w-full justify-between py-3 mb-2">
                                        <input
                                            type="text"
                                            placeholder="New tool name"
                                            className="border border-gray-300 rounded-l text-md px-4 py-3 rounded w-[70%]"
                                            id="newToolInput"
                                        />
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById('newToolInput') as HTMLInputElement;
                                                if (input && input.value.trim()) {
                                                    handleAddTool(editCategory.id, input.value.trim());
                                                    input.value = '';
                                                }
                                            }}
                                            className="px-4 py-3 bg-white text-black rounded hover:bg-gray-300 flex items-center gap-2 font-semibold border" 
                                        >
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Save
                                        </button>
                                    </div>
                                )
                            }

                            <div>
                                {tools[editCategory.id].map((tool: string, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-md mb-2"
                                    >
                                        <div className="flex items-center">
                                            <span className="text-gray-400 mr-2">#</span>
                                            <span>{tool}</span>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveTool(editCategory.id, index)}
                                            className="text-gray-500 hover:text-red-500"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FrameworksTab;
